import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lightbulb, Lock, Send, Loader2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Reveal, SectionHead } from "./Bits";
import { scrollToSupport } from "./content";
import { IdeaCard } from "./IdeaCard";

function Composer({ onList }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (text.trim().length < 5) { toast.error("Bitte beschreibe deine Idee etwas genauer"); return; }
    setBusy(true);
    try { const { data } = await api.post("/supporter-ideas", { text }); onList(data.ideas); setText(""); toast.success("Danke für deine Idee! 💡"); }
    catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="mt-6 rounded-3xl bg-white border border-pink-100 p-4 sm:p-5 shadow-[0_14px_34px_-24px_rgba(157,20,70,.45)]" data-testid="ideas-composer">
      <label htmlFor="idea-text" className="font-display text-base font-extrabold text-slate-900">Was wünschst du dir für unsere Community?</label>
      <textarea id="idea-text" value={text} onChange={(e) => setText(e.target.value.slice(0, 280))} rows={3} placeholder="z. B. ein neues Spiel, ein Geschenk, eine Funktion …" data-testid="ideas-input"
        className="mt-2 w-full rounded-2xl bg-rose-50/60 ring-1 ring-pink-100 px-4 py-3 text-sm sm:text-base outline-none focus:ring-2 focus:ring-rose-400 resize-none" />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-xs text-slate-400" data-testid="ideas-counter">{text.length}/280</span>
        <button type="submit" disabled={busy} data-testid="ideas-submit" className="h-11 px-5 rounded-full pink-gradient text-white text-sm font-extrabold inline-flex items-center gap-2 shadow-md shadow-rose-300/50 active:scale-95 transition-transform disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Idee einreichen
        </button>
      </div>
    </form>
  );
}

const Locked = ({ count }) => (
  <Reveal className="mt-6 relative overflow-hidden rounded-3xl border-2 border-dashed border-pink-200 bg-white/70 p-8 text-center" data-testid="ideas-locked">
    <span className="w-12 h-12 mx-auto rounded-2xl pink-gradient text-white flex items-center justify-center shadow-md shadow-rose-300/50"><Lock className="w-5 h-5" /></span>
    <p className="mt-3 font-display text-lg font-extrabold text-slate-900">Nur für Projekt Unterstützer</p>
    <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">{count > 0 ? `${count} ${count === 1 ? "Idee wurde" : "Ideen wurden"} schon eingereicht. ` : ""}Als Projekt Unterstützer kannst du hier Wünsche einreichen, mit Herzen abstimmen und unsere Antworten lesen.</p>
    <button onClick={scrollToSupport} data-testid="ideas-locked-cta" className="mt-4 h-11 px-5 rounded-full pink-gradient text-white text-sm font-extrabold shadow-md shadow-rose-300/50 active:scale-95 transition-transform">❤️ Projekt Unterstützer werden</button>
  </Reveal>
);

export function SupporterIdeas() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/supporter-ideas").then(({ data }) => setD(data)).catch(() => setD({ allowed: false, count: 0, ideas: [] })); }, []);
  useEffect(() => { if (d && window.location.hash === "#ideen") document.getElementById("ideen")?.scrollIntoView({ block: "start" }); }, [d]);
  const onList = (ideas) => setD((p) => ({ ...p, ideas, count: ideas.length }));
  return (
    <section id="ideen" className="scroll-mt-40 lg:scroll-mt-28" data-testid="cs-ideas" aria-labelledby="cs-ideas-title">
      <SectionHead id="cs-ideas-title" eyebrow="Mitgestalten" title="💡 Ideen-Wand der Unterstützer" sub="Reiche Wünsche für neue Funktionen ein und vergib Herzen für die Ideen der anderen. Wir lesen alles und antworten – eine bevorzugte Umsetzung können wir nicht garantieren." />
      {!d ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>
        : !d.allowed ? <Locked count={d.count} />
        : <>
            <Composer onList={onList} />
            {d.ideas.length === 0
              ? <p className="mt-5 flex items-center gap-2 text-sm text-slate-500" data-testid="ideas-empty"><Lightbulb className="w-4 h-4 text-amber-500" /> Noch keine Ideen – reiche die erste ein!</p>
              : <ul className="mt-5 grid gap-3 lg:grid-cols-2" data-testid="ideas-list">{d.ideas.map((i) => <IdeaCard key={i.id} i={i} admin={d.admin} onList={onList} />)}</ul>}
          </>}
    </section>
  );
}
